type Child = Node | string | null | undefined | false;

type Attrs = Record<string, string | number | boolean | ((e: never) => void) | undefined | null>;

/**
 * Terse element construction.
 *
 * The panels around the canvas are plain DOM, and a framework would be a lot of
 * machinery for a handful of forms. Attribute keys starting with `on` are bound
 * as listeners; everything else becomes an attribute.
 */
export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  children: Child[] = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs)) {
    if (value === undefined || value === null || value === false) continue;
    if (key.startsWith('on') && typeof value === 'function') {
      node.addEventListener(key.slice(2), value as EventListener);
    } else if (value === true) {
      node.setAttribute(key, '');
    } else {
      node.setAttribute(key, String(value));
    }
  }
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    node.appendChild(typeof child === 'string' ? document.createTextNode(child) : child);
  }
  return node;
}

/** Format bytes/milliseconds compactly for the status bar. */
export function formatMs(ms: number): string {
  return ms < 1 ? '<1 ms' : ms < 1000 ? `${Math.round(ms)} ms` : `${(ms / 1000).toFixed(2)} s`;
}

/** Trigger a browser download for text content. */
export function downloadText(filename: string, text: string, mime = 'application/json'): void {
  downloadBlob(filename, new Blob([text], { type: mime }));
}

export function downloadBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = el('a', { href: url, download: filename });
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoking immediately can race the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Ask the user for a file and return its text. Resolves undefined if cancelled. */
export function pickTextFile(accept: string): Promise<{ name: string; text: string } | undefined> {
  return new Promise((resolve) => {
    const input = el('input', { type: 'file', accept }) as HTMLInputElement;
    input.style.display = 'none';
    document.body.appendChild(input);
    // `cancel` is not universally supported, so a focus-based fallback keeps
    // the promise from hanging forever if the dialog is dismissed.
    let settled = false;
    const finish = (value: { name: string; text: string } | undefined) => {
      if (settled) return;
      settled = true;
      input.remove();
      resolve(value);
    };
    input.onchange = async () => {
      const file = input.files?.[0];
      finish(file ? { name: file.name, text: await file.text() } : undefined);
    };
    input.addEventListener('cancel', () => finish(undefined));
    input.click();
  });
}

/**
 * Copy a canvas, pixels included.
 *
 * `cloneNode` duplicates the element and its attributes but not the backing
 * bitmap, so a cloned canvas is silently blank — it looks like a rendering
 * failure rather than a copying one.
 */
export function cloneCanvas(source: HTMLCanvasElement): HTMLCanvasElement {
  const copy = document.createElement('canvas');
  copy.width = source.width;
  copy.height = source.height;
  copy.className = source.className;
  copy.getContext('2d')?.drawImage(source, 0, 0);
  return copy;
}
